import React from "react";
import { Workflow } from "../../../../../services/WorkflowService";
import { Button } from "../../../../ui/button";
import { Label } from "../../../../ui/label";
import { Input } from "../../../../ui/input";
import { Textarea } from "../../../../ui/textarea";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import {
  ArgBindingValueSource,
  ComputedArgBinding,
  GenerationVariable,
  VariableSource,
} from "./types";

export function variableDraftIsValid(draft: GenerationVariable): boolean {
  const name = draft.name.trim();
  if (!name || !/^\w+$/.test(name)) return false;
  if (draft.source === "computed" && !draft.workflow_ref.trim()) return false;
  return true;
}

function updateBindingAt(
  bindings: ComputedArgBinding[],
  index: number,
  patch: Partial<ComputedArgBinding>
): ComputedArgBinding[] {
  return bindings.map((b, i) => (i === index ? { ...b, ...patch } : b));
}

function removeBindingAt(bindings: ComputedArgBinding[], index: number): ComputedArgBinding[] {
  return bindings.filter((_, i) => i !== index);
}

function newComputedArgBinding(priorNames: string[]): ComputedArgBinding {
  const first = priorNames[0] || "";
  return {
    workflow_arg: "",
    value_source: priorNames.length > 0 ? "variable" : "static",
    variable: first,
    static_value: "",
  };
}

function bindingValueSource(binding: ComputedArgBinding): ArgBindingValueSource {
  return binding.value_source === "static" ? "static" : "variable";
}

function setBindingValueSource(
  bindings: ComputedArgBinding[],
  index: number,
  next: ArgBindingValueSource,
  priorNames: string[]
): ComputedArgBinding[] {
  const cur = bindings[index];
  if (!cur) return bindings;
  if (next === "static") {
    return updateBindingAt(bindings, index, {
      value_source: "static",
      variable: "",
      static_value: cur.static_value ?? "",
    });
  }
  const pick = priorNames.includes(cur.variable) ? cur.variable : priorNames[0] || "";
  return updateBindingAt(bindings, index, {
    value_source: "variable",
    variable: pick,
    static_value: "",
  });
}

interface EditVariableProps {
  draft: GenerationVariable;
  onDraftChange: (patch: Partial<GenerationVariable>) => void;
  priorNames: string[];
  workflows: Workflow[];
  title: string;
  onSave: () => void;
  onCancel: () => void;
  saveDisabled: boolean;
}

export function EditVariable({
  draft,
  onDraftChange,
  priorNames,
  workflows,
  title,
  onSave,
  onCancel,
  saveDisabled,
}: EditVariableProps) {
  const setSource = (source: VariableSource) => {
    if (source === "seed") {
      onDraftChange({ source: "seed", workflow_ref: "", arg_bindings: [], value_path: "" });
    } else {
      onDraftChange({ source: "computed" });
    }
  };

  const bindings = draft.arg_bindings || [];

  return (
    <div className="rounded-md border-2 border-maitai-green/40 bg-background shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-border px-3 py-2 bg-muted/30">
        <div className="min-w-0 pt-0.5">
          <div className="text-sm font-gc-med text-foreground">{title}</div>
          <button type="button" onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline mt-0.5">
            Cancel
          </button>
        </div>
        <Button type="button" size="sm" className="h-8 text-xs shrink-0" onClick={onSave} disabled={saveDisabled}>
          Save
        </Button>
      </div>
      <div className="p-3 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Name</Label>
            <Input
              value={draft.name}
              onChange={(e) => onDraftChange({ name: e.target.value.replace(/\W/g, "") })}
              placeholder="e.g. review_text"
              className="text-sm font-mono"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Source</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSource("seed")}
                className={cn(
                  "flex-1 rounded-md border-2 px-2 py-1.5 text-xs transition-colors",
                  draft.source === "seed" ? "border-maitai-green bg-maitai-green/5 font-gc-med" : "border-border"
                )}
              >
                Seed
              </button>
              <button
                type="button"
                onClick={() => setSource("computed")}
                className={cn(
                  "flex-1 rounded-md border-2 px-2 py-1.5 text-xs transition-colors",
                  draft.source === "computed" ? "border-maitai-green bg-maitai-green/5 font-gc-med" : "border-border"
                )}
              >
                Computed
              </button>
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Description</Label>
          <Textarea
            value={draft.description}
            onChange={(e) => onDraftChange({ description: e.target.value })}
            placeholder="What this placeholder represents — shown to the seed model when generating values"
            rows={2}
            className="text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Example value</Label>
          <Textarea
            value={draft.example}
            onChange={(e) => onDraftChange({ example: e.target.value })}
            placeholder="Sample text for this placeholder — combined with others to preview the filled user message template"
            rows={2}
            className="text-sm"
          />
        </div>
        {draft.source === "computed" && (
          <div className="space-y-2 pt-2 border-t border-border">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Workflow</Label>
              <select
                value={draft.workflow_ref}
                onChange={(e) => onDraftChange({ workflow_ref: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select a workflow...</option>
                {workflows.map((wf) => (
                  <option key={wf.workflow_ref_name} value={wf.workflow_ref_name}>
                    {wf.workflow_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs text-muted-foreground">Workflow input mapping</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() =>
                    onDraftChange({
                      arg_bindings: [...bindings, newComputedArgBinding(priorNames)],
                    })
                  }
                >
                  <PlusIcon className="w-3.5 h-3.5 mr-1" />
                  Add mapping
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Each row sets a workflow input field name. Choose <span className="font-gc-med">Variable</span> to pass a
                placeholder from above, or <span className="font-gc-med">Static</span> for a fixed value (numbers / JSON supported).
              </p>
              {priorNames.length === 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  No variables above yet — use Static values, or add a seed/computed variable above before mapping Variable rows.
                </p>
              )}
              {bindings.length > 0 && (
                <div className="space-y-2">
                  {bindings.map((binding, i) => (
                    <div
                      key={`binding-${i}`}
                      className="grid grid-cols-[minmax(0,4fr)_minmax(0,2fr)_minmax(0,4fr)_auto] gap-2 items-end p-2 rounded-md border border-border bg-muted/20"
                    >
                      <div className="space-y-1 min-w-0">
                        <Label className="text-[10px] text-muted-foreground">Workflow arg name</Label>
                        <Input
                          value={binding.workflow_arg}
                          onChange={(e) =>
                            onDraftChange({
                              arg_bindings: updateBindingAt(bindings, i, {
                                workflow_arg: e.target.value,
                              }),
                            })
                          }
                          placeholder="e.g. stop_code"
                          className="text-sm font-mono h-8"
                        />
                      </div>
                      <div className="space-y-1 min-w-0">
                        <Label className="text-[10px] text-muted-foreground">Value source</Label>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              onDraftChange({
                                arg_bindings: setBindingValueSource(bindings, i, "variable", priorNames),
                              })
                            }
                            className={cn(
                              "flex-1 rounded-md border-2 px-1.5 py-1 text-[11px] transition-colors h-8 whitespace-nowrap",
                              bindingValueSource(binding) === "variable"
                                ? "border-maitai-green bg-maitai-green/5 font-gc-med"
                                : "border-border"
                            )}
                          >
                            Variable
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              onDraftChange({
                                arg_bindings: setBindingValueSource(bindings, i, "static", priorNames),
                              })
                            }
                            className={cn(
                              "flex-1 rounded-md border-2 px-1.5 py-1 text-[11px] transition-colors h-8 whitespace-nowrap",
                              bindingValueSource(binding) === "static"
                                ? "border-maitai-green bg-maitai-green/5 font-gc-med"
                                : "border-border"
                            )}
                          >
                            Static
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1 min-w-0">
                        <Label className="text-[10px] text-muted-foreground">
                          {bindingValueSource(binding) === "variable" ? "From variable" : "Static value"}
                        </Label>
                        {bindingValueSource(binding) === "variable" ? (
                          <select
                            value={
                              priorNames.length
                                ? priorNames.includes(binding.variable)
                                  ? binding.variable
                                  : priorNames[0]
                                : ""
                            }
                            onChange={(e) =>
                              onDraftChange({
                                arg_bindings: updateBindingAt(bindings, i, {
                                  variable: e.target.value,
                                }),
                              })
                            }
                            disabled={priorNames.length === 0}
                            className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            {priorNames.length === 0 ? <option value="">—</option> : null}
                            {priorNames.map((n) => (
                              <option key={n} value={n}>
                                {`{{${n}}}`}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            value={binding.static_value ?? ""}
                            onChange={(e) =>
                              onDraftChange({
                                arg_bindings: updateBindingAt(bindings, i, {
                                  static_value: e.target.value,
                                }),
                              })
                            }
                            placeholder="e.g. 6, US, or a JSON object as text"
                            className="text-sm font-mono h-8"
                          />
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-destructive"
                        aria-label="Remove mapping"
                        onClick={() => onDraftChange({ arg_bindings: removeBindingAt(bindings, i) })}
                      >
                        <Trash2Icon className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Value path (dot path into workflow JSON)</Label>
              <Input
                value={draft.value_path}
                onChange={(e) => onDraftChange({ value_path: e.target.value })}
                placeholder="e.g. result.label"
                className="text-sm font-mono"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
